/**
 * Upload API - File upload functionality
 */

import { useMutation } from '@tanstack/react-query'
import { apiClient } from './client'
import type { UploadResponse, UploadedFileData } from '@/types/api.types'

/**
 * Upload file to backend
 */
async function uploadFile(file: File, sessionId: string): Promise<UploadedFileData> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('session_id', sessionId)

  const response = await apiClient.post<UploadResponse>('/upload_file', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    // Longer timeout for large files
    timeout: 60_000, // 60 seconds
  })

  if (response.data.status === 'error') {
    throw new Error(response.data.message || 'Upload failed')
  }

  return response.data.file_data
}

/**
 * Hook: Upload file (mutation)
 */
export function useUploadFile() {
  return useMutation({
    mutationFn: ({ file, sessionId }: { file: File; sessionId: string }) =>
      uploadFile(file, sessionId),
  })
}
